import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, FileText } from 'lucide-react';

function TradeQuoteRequest() {
  const navigate = useNavigate();
  const { token, API, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  const [formData, setFormData] = useState({
    order_type: 'Full Kitchen',
    notes: '',
    undermount_sink: false,
    overmount_sink: false,
    hob_cutout: false,
    socket_cutout: false,
    popup_socket: false,
    tap_hole: false,
    radius_corner: false,
    radius_corner_qty: 1,
    large_rounded_corner: false,
    large_rounded_corner_qty: 1,
    drainer_grooves: false,
    recessed_drainer: false,
    custom_fabrication_cost: 0,
    include_templating: true,
    include_fitting: true,
    edge_profile: ''
  });

  const [pieces, setPieces] = useState([{
    name: '',
    width_mm: '',
    height_mm: '',
    thickness: '20mm',
    edges: { top: false, right: false, bottom: false, left: false }
  }]);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/worktops/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const addPiece = () => {
    setPieces([...pieces, {
      name: '',
      width_mm: '',
      height_mm: '',
      thickness: '20mm',
      edges: { top: false, right: false, bottom: false, left: false }
    }]);
  };

  const removePiece = (index) => {
    setPieces(pieces.filter((_, i) => i !== index));
  };

  const duplicatePiece = (index) => {
    const pieceToDuplicate = pieces[index];
    const duplicated = {
      ...pieceToDuplicate,
      name: pieceToDuplicate.name ? `${pieceToDuplicate.name} (Copy)` : ''
    };
    const newPieces = [...pieces];
    newPieces.splice(index + 1, 0, duplicated);
    setPieces(newPieces);
  };

  const updatePiece = (index, field, value) => {
    const updated = [...pieces];
    updated[index][field] = value;
    setPieces(updated);
    
    const lastPiece = updated[updated.length - 1];
    const isLastPieceReady = lastPiece.width_mm && lastPiece.height_mm;
    
    if (isLastPieceReady && index === updated.length - 1) {
      setPieces([...updated, {
        name: '',
        width_mm: '',
        height_mm: '',
        thickness: '20mm',
        edges: { top: false, right: false, bottom: false, left: false }
      }]);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validPieces = pieces.filter(p => p.width_mm && p.height_mm);
      
      if (validPieces.length === 0) {
        toast.error('Please add at least one piece');
        setLoading(false);
        return;
      }

      // Upload files first if any
      let uploadedFileUrls = [];
      if (files.length > 0) {
        setUploading(true);
        const formData = new FormData();
        files.forEach(file => {
          formData.append('files', file);
        });

        const uploadResponse = await axios.post(`${API}/upload-files`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        uploadedFileUrls = uploadResponse.data.file_urls;
        setUploading(false);
      }

      const payload = {
        ...formData,
        pieces: validPieces,
        attached_files: uploadedFileUrls
      };

      await axios.post(`${API}/worktops/trade-quote-request`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Quote request submitted successfully! We will contact you soon.');
      navigate('/trade-dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to submit quote request'); toast.error(msg);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  if (!settings) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/trade-dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>Request a Quote</CardTitle>
            <p className="text-sm text-gray-600">Fill in the details and we'll send you a quote</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Order Type */}
              <div>
                <Label>Order Type</Label>
                <select
                  value={formData.order_type}
                  onChange={(e) => {
                    const isFullKitchen = e.target.value === 'Full Kitchen';
                    setFormData({ 
                      ...formData, 
                      order_type: e.target.value,
                      include_templating: isFullKitchen,
                      include_fitting: isFullKitchen
                    });
                  }}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Full Kitchen">Full Kitchen</option>
                  <option value="Offcut">Offcut</option>
                  <option value="Custom Fabrication">Custom Fabrication</option>
                  <option value="Fireplace/Hearth">Fireplace/Hearth</option>
                </select>
              </div>

              {/* Stone Pieces */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Stone Pieces</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {pieces.map((piece, index) => {
                      const areaM2 = (piece.width_mm && piece.height_mm) 
                        ? ((piece.width_mm * piece.height_mm) / 1000000).toFixed(2)
                        : '0.00';

                      return (
                        <div key={index} className="border rounded p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">Piece {index + 1}</h4>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => duplicatePiece(index)}
                                className="h-8 w-8 p-0"
                              >
                                <Plus className="w-4 h-4 text-blue-600" />
                              </Button>
                              {pieces.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removePiece(index)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Name (optional)</Label>
                              <Input
                                value={piece.name}
                                onChange={(e) => updatePiece(index, 'name', e.target.value)}
                                placeholder="e.g., Main Counter"
                              />
                            </div>
                            <div>
                              <Label>Thickness</Label>
                              <select
                                value={piece.thickness}
                                onChange={(e) => updatePiece(index, 'thickness', e.target.value)}
                                className="w-full px-3 py-2 border rounded-md"
                              >
                                <option value="20mm">20mm</option>
                                <option value="30mm">30mm</option>
                              </select>
                            </div>
                            <div>
                              <Label>Width (mm) {index === 0 && '*'}</Label>
                              <Input
                                type="number"
                                value={piece.width_mm}
                                onChange={(e) => updatePiece(index, 'width_mm', e.target.value)}
                                required={index === 0}
                              />
                            </div>
                            <div>
                              <Label>Height (mm) {index === 0 && '*'}</Label>
                              <Input
                                type="number"
                                value={piece.height_mm}
                                onChange={(e) => updatePiece(index, 'height_mm', e.target.value)}
                                required={index === 0}
                              />
                            </div>
                          </div>

                          <div>
                            <Label>Edges to Polish</Label>
                            <div className="relative" style={{ width: '60px', height: '50px', margin: '10px 0' }}>
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, top: !piece.edges.top })}
                                style={{ position: 'absolute', top: '0', left: '50%', transform: 'translateX(-50%)', width: '32px', height: '8px' }}
                                className={`${piece.edges.top ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                              />
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, right: !piece.edges.right })}
                                style={{ position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)', width: '8px', height: '32px' }}
                                className={`${piece.edges.right ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                              />
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, bottom: !piece.edges.bottom })}
                                style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', width: '32px', height: '8px' }}
                                className={`${piece.edges.bottom ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                              />
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, left: !piece.edges.left })}
                                style={{ position: 'absolute', left: '0', top: '50%', transform: 'translateY(-50%)', width: '8px', height: '32px' }}
                                className={`${piece.edges.left ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                              />
                              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '24px', height: '24px' }} className="bg-gray-100 border border-gray-400 rounded-sm pointer-events-none" />
                            </div>
                            <p className="text-xs text-gray-500">Blue = edges to polish</p>
                          </div>

                          <div className="text-sm">
                            <strong>Area:</strong> {areaM2} m²
                          </div>
                        </div>
                      );
                    })}
                    
                    <Button type="button" variant="outline" onClick={addPiece} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Another Piece
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Edge Profile */}
              <div>
                <Label>Edge Profile</Label>
                <select
                  value={formData.edge_profile}
                  onChange={(e) => setFormData({ ...formData, edge_profile: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">None</option>
                  {settings?.edge_profiles && 
                    Object.keys(settings.edge_profiles).map(profile => (
                      <option key={profile} value={profile}>{profile}</option>
                    ))
                  }
                </select>
              </div>

              {/* Extras */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Extras</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.undermount_sink}
                        onChange={(e) => setFormData({ ...formData, undermount_sink: e.target.checked })}
                      />
                      <span className="text-sm">Undermount Sink</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.overmount_sink}
                        onChange={(e) => setFormData({ ...formData, overmount_sink: e.target.checked })}
                      />
                      <span className="text-sm">Overmount Sink</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.hob_cutout}
                        onChange={(e) => setFormData({ ...formData, hob_cutout: e.target.checked })}
                      />
                      <span className="text-sm">Hob Cut-out</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.socket_cutout}
                        onChange={(e) => setFormData({ ...formData, socket_cutout: e.target.checked })}
                      />
                      <span className="text-sm">Socket Cut-out</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.tap_hole}
                        onChange={(e) => setFormData({ ...formData, tap_hole: e.target.checked })}
                      />
                      <span className="text-sm">Tap Hole</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.radius_corner}
                          onChange={(e) => setFormData({ ...formData, radius_corner: e.target.checked })}
                        />
                        <span className="text-sm">Radius Corner</span>
                      </label>
                      {formData.radius_corner && (
                        <Input
                          type="number"
                          min="1"
                          value={formData.radius_corner_qty}
                          onChange={(e) => setFormData({ ...formData, radius_corner_qty: e.target.value })}
                          className="w-16 h-8 px-2 text-sm"
                        />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.large_rounded_corner}
                          onChange={(e) => setFormData({ ...formData, large_rounded_corner: e.target.checked })}
                        />
                        <span className="text-sm">Large Rounded Corner</span>
                      </label>
                      {formData.large_rounded_corner && (
                        <Input
                          type="number"
                          min="1"
                          value={formData.large_rounded_corner_qty}
                          onChange={(e) => setFormData({ ...formData, large_rounded_corner_qty: e.target.value })}
                          className="w-16 h-8 px-2 text-sm"
                        />
                      )}
                    </div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.drainer_grooves}
                        onChange={(e) => setFormData({ ...formData, drainer_grooves: e.target.checked })}
                      />
                      <span className="text-sm">Drainer Grooves</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.recessed_drainer}
                        onChange={(e) => setFormData({ ...formData, recessed_drainer: e.target.checked })}
                      />
                      <span className="text-sm">Recessed Drainer</span>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Services */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.include_templating}
                        onChange={(e) => setFormData({ ...formData, include_templating: e.target.checked })}
                      />
                      <span className="text-sm">Professional Templating Service</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.include_fitting}
                        onChange={(e) => setFormData({ ...formData, include_fitting: e.target.checked })}
                      />
                      <span className="text-sm">Professional Fitting Service</span>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  placeholder="Any special requirements or additional information..."
                />
              </div>

              {/* File Upload */}
              <Card>
                <CardHeader>
                  <CardTitle>Attach Drawings / Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="file-upload" className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-500 transition-colors">
                          <input
                            id="file-upload"
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                          <div className="flex flex-col items-center gap-2">
                            <FileText className="w-10 h-10 text-gray-400" />
                            <p className="text-sm text-gray-600">Click to upload files</p>
                            <p className="text-xs text-gray-400">PDF, JPG, PNG, DOC (Max 10MB each)</p>
                          </div>
                        </div>
                      </Label>
                    </div>

                    {files.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Selected Files:</p>
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <span className="text-sm truncate flex-1">{file.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/trade-dashboard')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default TradeQuoteRequest;
